import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  generateReefSuggestions,
  getAquariumInsight,
  listOpenReefSuggestions,
  refreshAquariumInsight,
} from '../services/insightService';
import type { InsightResponse, ReefSuggestion } from '../services/insightService';
import { invalidateAiUsage } from './useAiUsage';

export const REEF_INSIGHT_QUERY_KEY = 'reef-insight';

/**
 * Open suggestions live under the same prefix as useAiSuggestions'
 * `['ai-suggestions', 'aquarium', id]`, so the existing accept / dismiss /
 * edit-accept mutations (which invalidate that prefix) refresh this list too.
 */
export function reefSuggestionsQueryKey(aquariumId: string) {
  return ['ai-suggestions', 'aquarium', aquariumId, 'open'] as const;
}

export function reefInsightQueryKey(aquariumId: string) {
  return [REEF_INSIGHT_QUERY_KEY, aquariumId] as const;
}

export interface UseReefInsightResult {
  data: InsightResponse | undefined;
  isLoading: boolean;
  error: unknown;
  retry: () => void;
  refresh: () => void;
  isRefreshing: boolean;
  refreshError: unknown;
}

/**
 * Reads the aquarium's Reef Intelligence insight. Reading never writes tasks
 * or suggestions (the backend guarantees GET /insights is read-only).
 */
export function useReefInsight(aquariumId: string | undefined): UseReefInsightResult {
  const queryClient = useQueryClient();
  const id = aquariumId ?? '';

  const query = useQuery({
    queryKey: reefInsightQueryKey(id),
    queryFn: async () => {
      // A non-cached insight is an AI call: keep the credit meter current.
      try {
        return await getAquariumInsight(id);
      } finally {
        invalidateAiUsage(queryClient);
      }
    },
    enabled: id !== '',
    // Always ask the backend: it only calls the AI when an ICP analysis, a
    // measurement or a dosing plan changed, otherwise it answers from the saved
    // insight. So new data shows up on the next dashboard visit.
    staleTime: 0,
    retry: false,
  });

  const refreshMutation = useMutation({
    mutationFn: () => refreshAquariumInsight(id),
    onSuccess: (result) => {
      queryClient.setQueryData(reefInsightQueryKey(id), result);
    },
    onSettled: () => {
      invalidateAiUsage(queryClient);
    },
  });

  return {
    data: query.data,
    isLoading: query.isPending && id !== '',
    error: query.error,
    retry: () => {
      void query.refetch();
    },
    refresh: () => {
      refreshMutation.mutate();
    },
    isRefreshing: refreshMutation.isPending,
    refreshError: refreshMutation.error,
  };
}

export interface UseReefSuggestionsResult {
  suggestions: ReefSuggestion[];
  isLoading: boolean;
  error: unknown;
  retry: () => void;
  generate: () => void;
  isGenerating: boolean;
  generateError: unknown;
}

/**
 * Open (status `suggested`) AI suggestions for the aquarium, plus the explicit
 * "generate" action that materialises the current insight's recommendations
 * as suggestions. Generating never creates a task.
 */
export function useReefSuggestions(aquariumId: string | undefined, enabled = true): UseReefSuggestionsResult {
  const queryClient = useQueryClient();
  const id = aquariumId ?? '';

  const query = useQuery({
    queryKey: reefSuggestionsQueryKey(id),
    queryFn: () => listOpenReefSuggestions(id),
    enabled: enabled && id !== '',
    staleTime: 60_000,
    retry: false,
  });

  const generateMutation = useMutation({
    mutationFn: () => generateReefSuggestions(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ai-suggestions', 'aquarium', id] });
    },
    onSettled: () => {
      invalidateAiUsage(queryClient);
    },
  });

  return {
    suggestions: query.data ?? [],
    isLoading: query.isPending && enabled && id !== '',
    error: query.error,
    retry: () => {
      void query.refetch();
    },
    generate: () => {
      generateMutation.mutate();
    },
    isGenerating: generateMutation.isPending,
    generateError: generateMutation.error,
  };
}
